/**
 * Composant principal du quiz
 * Gère l'affichage des questions, les réponses, le timer et le calcul du score
 */
import { useState, useEffect } from 'react';
import quizService from '../api/quizService';
import { Timer } from '../components/Timer';
import { BoutonQuest } from '../components/BoutonQuest';
import { useAuth } from '../hooks/useAuth';
import { setCookie } from '../utils/cookieUtils';
import { QuizResults } from './QuizResults';
import '../styles/quiz.css';

/**
 * Quiz - Composant d'affichage et de gestion du quiz
 * @param {Object} props - Les propriétés du composant
 * @param {number} props.categoryId - L'identifiant de la catégorie de quiz
 * @param {string} props.category - Le diminutif de la catégorie (HST, SP, GEO, CIN, JV, MUS)
 * @param {function} props.onExit - Fonction appelée pour quitter le quiz
 * @returns {JSX.Element} Interface du quiz avec questions et réponses
 */
export default function Quiz({ categoryId, category, onExit }) {
    const { user } = useAuth();
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [quizFinished, setQuizFinished] = useState(false);
    const [answerStatuses, setAnswerStatuses] = useState({});
    const [questionStatus, setQuestionStatus] = useState('');
    const [quizStartTime] = useState(new Date().toISOString());
    const [sendingScore, setSendingScore] = useState(false);
    const [currentTimeLeft, setCurrentTimeLeft] = useState(30);
    const [questionTimes, setQuestionTimes] = useState([]);

    useEffect(() => {
        const loadQuiz = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await quizService.getQuiz(categoryId);
                
                // L'API retourne { categorie: "...", questions: [...] }
                const questionsArray = data.questions || data;
                
                if (Array.isArray(questionsArray) && questionsArray.length > 0) {
                    setQuestions(questionsArray);
                } else {
                    setError('Aucune question disponible pour cette catégorie');
                }
                setLoading(false);
            } catch (error) {
                console.error('Erreur lors du chargement du quiz:', error);
                setError(error.message || 'Erreur lors du chargement du quiz');
                setLoading(false);
            }
        };

        if (categoryId) {
            loadQuiz();
        }
    }, [categoryId]);

    useEffect(() => {
        const recordQuizScore = async () => {
            if (quizFinished && user && user.idCompte && questions.length > 0 && !sendingScore) {
                setSendingScore(true);
                try {
                    const quizEndTime = new Date().toISOString();
                    await quizService.recordGame(
                        user.idCompte,
                        categoryId,
                        score,
                        questions.length,
                        quizStartTime,
                        quizEndTime
                    );
                } catch (error) {
                    console.error('Erreur lors de l\'enregistrement du score:', error);
                }
            }
        };

        recordQuizScore();
    }, [quizFinished, user, categoryId, score, questions.length, quizStartTime, sendingScore]);

    useEffect(() => {
        // Sauvegarde le score final dans un cookie quand le quiz est terminé
        if (quizFinished && questions.length > 0) {
            const totalTime = questionTimes.reduce((acc, time) => acc + time, 0);
            const scoreData = {
                score: score,
                totalQuestions: questions.length,
                percentage: Math.round((score / questions.length) * 100),
                category: category,
                categoryId: categoryId,
                timestamp: new Date().toISOString(),
                totalTime: totalTime
            };
            setCookie('quizLastScore', JSON.stringify(scoreData), 7); // Valable 7 jours
            // Sauvegarde aussi les temps individuels pour plus de détails
            setCookie('quizQuestionTimes', JSON.stringify(questionTimes), 7);
        }
    }, [quizFinished, score, questions.length, category, categoryId, questionTimes]);

    const handleAnswerClick = (answerId, isCorrect) => {
        if (isAnswered) return;

        setSelectedAnswer(answerId);
        setIsAnswered(true);
        setQuestionStatus(isCorrect ? 'correct' : 'incorrect');

        // Enregistre le temps passé sur cette question
        const timeTaken = 30 - currentTimeLeft;
        setQuestionTimes([...questionTimes, timeTaken]);

        const newStatuses = {};
        currentQuestion.reponses.forEach((rep) => {
            if (rep.idRep === answerId) {
                newStatuses[rep.idRep] = isCorrect ? 'correct' : 'incorrect';
            } else if (rep.isBonneRep) {
                newStatuses[rep.idRep] = 'correct';
            }
        });
        setAnswerStatuses(newStatuses);

        if (isCorrect) {
            setScore(score + 1);
        }

        setTimeout(() => {
            goToNextQuestion();
        }, 2000);
    };

    const handleTimeUp = () => {
        if (isAnswered) return;

        setIsAnswered(true);
        setQuestionStatus('incorrect');
        
        // Enregistre le temps complet (30 secondes) puisque le temps est écoulé
        setQuestionTimes([...questionTimes, 30]);
        
        const newStatuses = {};
        currentQuestion.reponses.forEach((rep) => {
            if (rep.isBonneRep) {
                newStatuses[rep.idRep] = 'correct';
            }
        });
        setAnswerStatuses(newStatuses);

        setTimeout(() => {
            goToNextQuestion();
        }, 2000);
    };

    const goToNextQuestion = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setSelectedAnswer(null);
            setIsAnswered(false);
            setAnswerStatuses({});
            setQuestionStatus('');
        } else {
            setQuizFinished(true);
        }
    };

    const handleRestart = () => {
        setCurrentIndex(0);
        setScore(0);
        setSelectedAnswer(null);
        setIsAnswered(false);
        setQuizFinished(false);
        setAnswerStatuses({});
        setQuestionStatus('');
    };

    const currentQuestion = questions[currentIndex];

    if (loading) {
        return (
            <div className="quiz-container">
                <div className="quiz-loading">
                    <p>Chargement du quiz...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="quiz-container">
                <div className="quiz-error">
                    <p>Erreur: {error}</p>
                    <button onClick={onExit} className="btn-exit">Retour</button>
                </div>
            </div>
        );
    }

    if (questions.length === 0) {
        return (
            <div className="quiz-container">
                <div className="quiz-error">
                    <p>Aucune question disponible</p>
                    <button onClick={onExit} className="btn-exit">Retour</button>
                </div>
            </div>
        );
    }

    if (quizFinished) {
        return <QuizResults onRestart={handleRestart} onExit={onExit} />;
    }

    if (!currentQuestion) {
        return (
            <div className="quiz-container">
                <div className="quiz-error">
                    <p>Question introuvable</p>
                    <button onClick={onExit} className="btn-exit">Retour</button>
                </div>
            </div>
        );
    }

    const niveauLabels = {
        1: 'Facile',
        2: 'Moyen',
        3: 'Difficile'
    };

    return (
        <div className="quiz-container" data-category={category}>
            <div className={`quiz-header ${questionStatus}`}>
                <button onClick={onExit} className="btn-close">✕</button>
                <Timer 
                    duration={30} 
                    onTimeUp={handleTimeUp} 
                    questionIndex={currentIndex}
                    onTimeChange={setCurrentTimeLeft}
                />
                <div className="quiz-score">{score * 100} pts</div>
            </div>

            <div className="quiz-content">
                <div className={`question-box ${questionStatus}`} data-category={category}>
                    <p className="question-info">
                        Question {currentIndex + 1} | {niveauLabels[currentQuestion.question.niveau] || 'Moyen'}
                    </p>
                    <h2 className="question-text" data-category={category}>{currentQuestion.question.libelleque}</h2>
                </div>
                <div className={`question-coupole ${questionStatus}`} data-category={category}></div>


                <div className="answers-container">
                    {currentQuestion.reponses && currentQuestion.reponses.map((answer, idx) => (
                        <div
                            key={answer.idRep}
                            onClick={() => handleAnswerClick(answer.idRep, answer.isBonneRep)}
                            style={{ cursor: isAnswered ? 'default' : 'pointer' }}
                        >
                            <BoutonQuest
                                index={idx + 1}
                                libelleRep={answer.libelleRep}
                                status={answerStatuses[answer.idRep] || ''}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
